import React, { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { usePermit } from '../context/PermitContext';
import { useGuru } from '../context/GuruContext';
import { useUserAccess } from '../context/UserAccessContext';
import { LoadingSpinner } from './LoadingSpinner';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/** Halaman satu-satunya yang terbuka bagi pegawai baru yang belum melengkapi data diri. */
export const RUTE_LENGKAPI_DATA = '/portal-guru/profil/informasi-pribadi';

export const ActiveGuruRoute: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { authUser, loading: authLoading } = useAuth();
  const { loading: permitsLoading } = usePermit();
  const { allGuruRecords } = useGuru();
  const { isSuperAdmin, isAkunLayanan, isAkunLayananNonaktif, loading: accessLoading } = useUserAccess();
  const location = useLocation();

  const [checkingChildren, setCheckingChildren] = useState<boolean>(false);
  const [hasChildren, setHasChildren] = useState<boolean | null>(null);

  useEffect(() => {
    const checkChildren = async () => {
      if (authLoading || permitsLoading || accessLoading || !authUser || isSuperAdmin || isAkunLayanan) return;

      const currentGuru = allGuruRecords.find(g => g.email === authUser.email);
      if (currentGuru && currentGuru.statusKeaktifan === 'Nonaktif') {
        if (currentGuru.noKk) {
          setCheckingChildren(true);
          try {
            const q = query(collection(db, 'santri'), where('noKk', '==', currentGuru.noKk));
            const snapshot = await getDocs(q);
            setHasChildren(!snapshot.empty);
          } catch (error) {
            console.error("Error checking santri children:", error);
            setHasChildren(false);
          } finally {
            setCheckingChildren(false);
          }
        } else {
          setHasChildren(false);
        }
      }
    };

    checkChildren();
  }, [authUser, authLoading, permitsLoading, accessLoading, allGuruRecords, isSuperAdmin, isAkunLayanan]);

  if (authLoading || permitsLoading || accessLoading || checkingChildren) {
    return <LoadingSpinner />;
  }

  if (!authUser) {
    return <Navigate to="/" replace />;
  }

  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // Akun layanan yang dinonaktifkan di Hak Akses (docs/PANDUAN_AKUN_LAYANAN.md).
  if (isAkunLayananNonaktif) {
    return <Navigate to="/nonaktif" replace />;
  }

  // Status di Data Pegawai tidak berlaku untuk akun layanan: aktif/nonaktifnya diatur di Hak Akses.
  const currentGuru = isAkunLayanan ? undefined : allGuruRecords.find(g => g.email === authUser.email);
  if (currentGuru && currentGuru.statusKeaktifan === 'Nonaktif') {
    if (hasChildren === true) {
      return <Navigate to="/portal-wali" replace />;
    }
    if (hasChildren === false) {
      return <Navigate to="/nonaktif" replace />;
    }
    // Still resolving (should be covered by checkingChildren spinner)
    return <LoadingSpinner />;
  }

  // Pegawai baru dari persetujuan cepat: semua menu terkunci sampai data diri lengkap
  // (docs/PANDUAN_PERSETUJUAN_GURU_BARU.md).
  if (currentGuru?.perluLengkapiData && location.pathname !== RUTE_LENGKAPI_DATA) {
    return <Navigate to={RUTE_LENGKAPI_DATA} replace />;
  }

  return <>{children}</>;
};
